import { TZDate, TZDateMini } from '@date-fns/tz';
import {
  addDays,
  addWeeks,
  compareAsc,
  endOfDay,
  endOfWeek,
  intervalToDuration,
  isBefore,
  isEqual,
  isSameDay,
  isWithinInterval,
  lastDayOfMonth,
  lastDayOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
// This should be replaced with the Intl Locale weekInfo API (https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/Locale/weekInfo) as soon as it gets full support. It's currently not supported in Firefox, but there is an open issue for that - https://bugzilla.mozilla.org/show_bug.cgi?id=1693576
// TODO: remove as soon as Firefox gets support
import { getWeekStartByLocale } from 'weekstart';

import {
  getTimezone,
  getRegionalFormat,
} from '@dynatrace-sdk/user-preferences';

import { isTimeframe, isTimeValue } from '../../../core/time/helpers.js';
import type { Precision } from '../../../core/time/shared.types.js';
import type {
  CalendarTimeframeState,
  CalendarTimeframeStore,
  StringTimeOffset,
} from '../../../core/time/timeframeStore/calendar.types.js';
import { type TimeValue, type Timeframe } from '../../../core/types/time.js';
import {
  getTimeValueFromSimpleValue,
  sortTimeframeValues,
  eachDayOfInterval,
  timeValueToDate,
} from '../../forms-core/time/helpers.js';
import {
  isExpressionString,
  mapToPartialTimeframe,
  removePrecision,
  removePrecisionFromTimeValue,
} from '../../forms-core/time/timeParser.js';
import type {
  CalendarProps,
  CalendarStrictValueType,
} from '../Calendar.types.js';
import type { ViewItem } from '../components/CalendarItem.js';
import {
  NUMBER_OF_WEEKDAYS,
  NUMBER_OF_WEEK_ROWS,
  type CalendarType,
} from '../types/calendarConfiguration.js';
import type { FirstDayOfWeek } from '../types/shared-types.js';

/** @internal */
export interface Day {
  date: TZDate;
  isCurrentMonthDay: boolean;
}

/**
 * Get days to render in the select date view.
 * @internal
 */
export function getDays(
  activeDate: TZDate,
  weekStartsOn: FirstDayOfWeek,
): Day[] {
  const monthStart = startOfMonth(activeDate);

  const firstWeekStart = startOfWeek(monthStart, {
    weekStartsOn,
  });

  const monthEnd = lastDayOfMonth(monthStart);
  const lastWeekEnd = lastDayOfWeek(monthEnd, { weekStartsOn });

  // Get days of previous month to fill empty spots of the date grid
  let prevMonthDays = getSiblingMonthDays(monthStart, firstWeekStart, -1);

  const currentMonthDays = toDayArray(
    eachDayOfInterval({
      start: monthStart,
      end: monthEnd,
    }),
    true,
  );

  // Get days of next month to fill empty spots of the date grid
  const nextMonthDays = getSiblingMonthDays(monthEnd, lastWeekEnd, 1);

  const days = [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];

  // Depending on the start/end day of the month, we might need additional rows
  // to fill up all the empty spaces of the date grid
  while (days.length < NUMBER_OF_WEEKDAYS * NUMBER_OF_WEEK_ROWS) {
    if (!prevMonthDays.length) {
      // Add additional row of sibling month days before the current month
      prevMonthDays = toDayArray(
        eachDayOfInterval({
          // Typecast to TZDate since TypeScript doesn't infer addWeeks return type directly
          start: addWeeks<TZDate, TZDate>(firstWeekStart, -1),
          end: addDays<TZDate, TZDate>(firstWeekStart, -1),
        }),
      );

      days.unshift(...prevMonthDays);
    } else {
      // Add additional row of sibling month days after the current month
      days.push(
        ...toDayArray(
          eachDayOfInterval({
            // Typecast to TZDate since TypeScript doesn't infer addWeeks return type directly
            start: addDays<TZDate, TZDate>(lastWeekEnd, 1),
            end: addWeeks<TZDate, TZDate>(lastWeekEnd, 1),
          }),
        ),
      );
    }
  }

  return days;
}

/** Get previous or next month's days to fill the current date select view with the fixed number of rows. */
function getSiblingMonthDays(
  monthStartEnd: TZDate,
  weekStartEnd: TZDate,
  direction: 1 | -1,
): Day[] {
  // No need to fill empty spots if month start and week start are equal
  if (isEqual(monthStartEnd, weekStartEnd)) {
    return [];
  }

  // Get first/last day of sibling month
  const siblingMonthStartEnd = addDays(monthStartEnd, direction);

  // The range start date must be prior to the range end date -> sort dates
  const sortedDays = [siblingMonthStartEnd, weekStartEnd].sort((a, b) =>
    compareAsc(a, b),
  );

  // Return array with days to fill up the empty spots of the currently displayed month
  return toDayArray(
    eachDayOfInterval({
      start: sortedDays[0],
      end: sortedDays[1],
    }),
  );
}

/** Map array of dates to an array of Day objects */
function toDayArray(
  array: TZDate[],
  isCurrentMonthDay: boolean | undefined = false,
): Day[] {
  return array.map(
    (date): Day => ({
      date,
      isCurrentMonthDay,
    }),
  );
}

/**
 * Tries to convert the given value to the proper TimeValue / Timeframe structure. If not possible it returns null.
 * @internal
 */
export function convertToCalendarStructure<Type extends CalendarType>(
  type: CalendarType = 'single',
  value?:
    | Partial<Timeframe>
    | Partial<{ from: string; to: string }>
    | null
    | string
    | Date
    | TimeValue
    | CalendarTimeframeState['calendarValue'],
  min?: TimeValue | null,
  max?: TimeValue | null,
  precision?: CalendarProps<Type>['precision'],
): CalendarTimeframeState['calendarValue'] {
  if (type === 'single' && !value) {
    return null;
  }

  if (typeof value === 'string' || value instanceof Date) {
    const stringDate = typeof value === 'string' ? value : value.toISOString();
    const timeOffset = getTimeOffsetFromInitialValue(
      stringDate,
      'single',
      min,
      max,
      precision,
    );

    return (
      getTimeValueFromSimpleValue(
        value,
        timeOffset,
        undefined,
        undefined,
        min,
        max,
        precision,
      ) ?? null
    );
  }

  if (isTimeValue(value)) {
    return removePrecisionFromTimeValue(value, precision);
  }

  if (value) {
    const timeframe = mapToPartialTimeframe(value);
    const timeOffset = getTimeOffsetFromInitialValue(
      timeframe,
      'range',
      min,
      max,
      precision,
    );

    const from = getTimeValueFromSimpleValue(
      timeframe?.from?.value,
      timeOffset,
      'from',
      timeframe?.from?.type === 'expression',
      min,
      undefined,
      precision,
    );
    const to = getTimeValueFromSimpleValue(
      timeframe?.to?.value,
      timeOffset,
      'to',
      timeframe?.to?.type === 'expression',
      undefined,
      max,
      precision,
    );
    return sortTimeframeValues({ from, to }, min, max, precision);
  }
  return { from: null, to: null };
}

/** @internal */
function getRangePosition(newDate: TZDate, from: TZDate, to: TZDate) {
  if (from && isSameDay(newDate, from)) {
    return 'first';
  }

  if (to && isSameDay(newDate, to)) {
    return 'last';
  }

  if (
    isWithinInterval(newDate, {
      start: startOfDay(from),
      end: endOfDay(to),
    })
  ) {
    return 'inbetween';
  }
}

/** @internal */
function isRelativeDate(
  highlightType: ViewItem['highlightType'],
  selectedDate: Partial<Timeframe>,
): ViewItem['selectionVariant'] {
  switch (highlightType) {
    case 'first': {
      if (selectedDate.from) {
        return selectedDate.from.type === 'iso8601' ? 'absolute' : 'relative';
      }
      return undefined;
    }
    case 'last': {
      if (selectedDate.to) {
        return selectedDate.to.type === 'iso8601' ? 'absolute' : 'relative';
      }
      // if the to is selected first, an earlier date should become the from in the end, therefore
      if (!selectedDate.to && selectedDate.from) {
        return selectedDate.from.type === 'iso8601' ? 'absolute' : 'relative';
      }

      return undefined;
    }
    case 'same': {
      if (
        selectedDate.from?.type === 'expression' &&
        selectedDate.to?.type === 'expression'
      ) {
        return 'relative';
      }

      if (
        selectedDate.from?.type === 'expression' &&
        selectedDate.to?.type === 'iso8601'
      ) {
        return 'relative-absolute';
      }

      if (
        selectedDate.from?.type === 'iso8601' &&
        selectedDate.to?.type === 'expression'
      ) {
        return 'absolute-relative';
      }

      return 'absolute';
    }
    case 'inbetween':
    default: {
      return undefined;
    }
  }
}

/** @internal */
function getHighlightType(
  from: TZDate | undefined | null,
  to: TZDate | undefined | null,
  newDate: TZDate,
) {
  if (
    (to && from && isSameDay(from, newDate) && isSameDay(to, newDate)) ||
    (!to && from && isSameDay(from, newDate)) ||
    (!from && to && isSameDay(to, newDate))
  ) {
    return 'same';
  }

  if (from && to && isBefore(from, to)) {
    return getRangePosition(newDate, from, to);
  }

  if (from && to && isBefore(to, from)) {
    return getRangePosition(newDate, to, from);
  }

  return undefined;
}

function getSelectionVariant(
  isSelectedFrom: boolean,
  isSelectedTo: boolean,
  selectedFrom: Partial<Timeframe>['from'],
  selectedTo: Partial<Timeframe>['to'],
  highlightType: ViewItem['highlightType'],
  selectedDate: Partial<Timeframe>,
): ViewItem['selectionVariant'] {
  if (isSelectedFrom && isSelectedTo) {
    return isRelativeDate('same', selectedDate);
  }
  if (isSelectedFrom) {
    return selectedFrom?.type === 'iso8601' ? 'absolute' : 'relative';
  }
  if (isSelectedTo) {
    return selectedTo?.type === 'iso8601' ? 'absolute' : 'relative';
  }
  return isRelativeDate(highlightType, selectedDate);
}

/**
 * Returns highlighting properties for a date cell when a timeframe (range) is selected.
 * Determines selection state, highlight color/type, and selection variant for the calendar view.
 */
const getHighlightPropsFromTimeframe = (
  date: TZDate,
  selectedDate: Partial<Timeframe>,
  hoveredDate: TZDate | null | undefined,
): Pick<
  ViewItem,
  | 'isSelected'
  | 'highlightColor'
  | 'highlightType'
  | 'selectionVariant'
  | 'isIncompleteSelection'
> => {
  const { from: selectedFrom, to: selectedTo } = selectedDate;
  if (!selectedFrom && !selectedTo) {
    return {};
  }

  const relativeDate = TZDateMini.tz(getTimezone()).getTime();
  const from = timeValueToDate(selectedFrom, relativeDate);
  const to = timeValueToDate(selectedTo, relativeDate);

  const isSelected = !!(
    (from && isSameDay(date, from)) ||
    (to && isSameDay(date, to))
  );
  const safeFromDate = from ?? hoveredDate;
  const safeToDate = to ?? hoveredDate;

  const highlightType = getHighlightType(safeFromDate, safeToDate, date);
  const isEntireRangeSelected =
    !!selectedFrom && !!selectedTo && !!highlightType;

  const highlightColor =
    isSelected || isEntireRangeSelected ? 'primary' : 'neutral';

  const isSelectedFrom = !!(from && selectedFrom && isSameDay(date, from));
  const isSelectedTo = !!(to && selectedTo && isSameDay(date, to));

  const selectionVariant = isSelected
    ? getSelectionVariant(
        isSelectedFrom,
        isSelectedTo,
        selectedFrom,
        selectedTo,
        highlightType,
        selectedDate,
      )
    : undefined;

  return {
    highlightType,
    highlightColor,
    isSelected,
    isIncompleteSelection: !isEntireRangeSelected,
    selectionVariant,
  };
};

/**
 * Checks if the passed date is the selected date or part of the selected date range.
 * Depending on the selection it defines the highlightColor, the highlightType and if it is a relative timeframe.
 * @internal
 */
export const getHighlightingProps = (
  date: TZDate,
  selectedDate: CalendarTimeframeStore['calendarValue'],
  type: CalendarType,
  hoveredDate: TZDate | null = null,
): Pick<
  ViewItem,
  | 'isSelected'
  | 'highlightColor'
  | 'highlightType'
  | 'selectionVariant'
  | 'isIncompleteSelection'
> => {
  if (!selectedDate) {
    return {};
  }

  // Range date selection
  if (isTimeframe(selectedDate, type)) {
    return getHighlightPropsFromTimeframe(date, selectedDate, hoveredDate);
  }

  // Single date selection
  if (isTimeValue(selectedDate)) {
    const newSelectedDate = timeValueToDate(selectedDate, Date.now());
    if (!newSelectedDate) {
      return {};
    }

    const isSelected = isSameDay(date, newSelectedDate);
    if (isSelected) {
      return {
        isSelected: true,
        highlightType: 'same',
        highlightColor: 'primary',
        isIncompleteSelection: false,
        selectionVariant:
          selectedDate.type === 'expression' ? 'relative' : 'absolute',
      };
    }

    return {
      isSelected: false,
    };
  }
  return {};
};

/**
 * Returns the number of days from the start of the week until the current date.
 * @internal
 */
export function getWeekStartOrEnd(
  indicator: 'startOfWeek' | 'endOfWeek',
  activeDate: TZDate,
) {
  const region = getRegionalFormat();
  const weekStartsOn = getWeekStartByLocale(region);
  const start =
    indicator === 'startOfWeek'
      ? startOfWeek(activeDate, { weekStartsOn })
      : endOfWeek(activeDate, { weekStartsOn });

  return (
    (intervalToDuration({
      start,
      end: activeDate,
    }).days ?? 0) * -1
  );
}

/** @internal */
export function getTimeOffsetFromInitialValue<Type extends CalendarType>(
  value?:
    | CalendarStrictValueType<Type>
    | { from: string | null | TimeValue; to: string | null | TimeValue }
    | TimeValue,
  type: CalendarType = 'single',
  min: TimeValue | null = null,
  max: TimeValue | null = null,
  precision: Precision = 'milliseconds',
): StringTimeOffset {
  if (isTimeframe(value, type)) {
    if (!value) {
      return { from: 'startOfDay', to: 'endOfDay' };
    }

    let from = value['from']?.absoluteDate;
    let to = value['to']?.absoluteDate;
    const configuredTimezone = getTimezone();

    const fromIsAtStartOfDay =
      from &&
      from ===
        startOfDay(new TZDateMini(from, configuredTimezone)).toISOString();
    const fromIsMin =
      min &&
      from &&
      removePrecision(from, precision) ===
        removePrecision(min?.absoluteDate, precision);

    // we need to check if the absoluteDate is set and would match the start of day in the configured timezone (which could be on a different date than the absoluteDate) or if a relative expressions is used
    if (
      !from ||
      fromIsAtStartOfDay ||
      value['from']?.type === 'expression' ||
      fromIsMin
    ) {
      from = 'startOfDay';
    }

    const toIsAtEndOfDay =
      to &&
      to === endOfDay(new TZDateMini(to, configuredTimezone)).toISOString();
    const toIsMax =
      max &&
      to &&
      removePrecision(to, precision) ===
        removePrecision(max?.absoluteDate, precision);

    // we need to check if the absoluteDate is set and would match the end of day in the configured timezone (which could be on a different date than the absoluteDate) or if a relative expressions is used
    if (!to || toIsAtEndOfDay || value.to?.type === 'expression' || toIsMax) {
      to = 'endOfDay';
    }

    return {
      from,
      to,
    };
  }

  if (precision === 'day') {
    return 'startOfDay';
  }

  const offset = isTimeValue(value) ? value.value : value;

  if (typeof offset === 'string') {
    return isExpressionString(offset) ? 'startOfDay' : offset;
  }

  return 'startOfDay';
}
